/**
 * Afrikaans: Common words
 *
 * What this file covers: Buttons, statuses, table controls, short notices, countries, phone numbers, password
 * rules and contact attributes used on many pages.
 *
 * Tone: The shortest everyday words. Buttons are verbs (Save, Cancel, Remove). Use one word per idea and never
 * vary it, because these words sit beside every other file's text.
 *
 * Afrikaans: Standard South African Afrikaans. Address the reader as "jy". Use the same Afrikaans word for the
 * same English term everywhere; menu.ts and common.ts set the words the other files follow. Afrikaans must stay
 * complete: the release checks fail if a key is missing.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const common: Catalog = {
  'action.signOut': 'Teken uit',
  'action.save': 'Stoor',
  'action.cancel': 'Kanselleer',
  'action.create': 'Skep',
  'action.delete': 'Skrap',
  'action.search': 'Soek',
  'common.loading': 'Laai tans…',
  'common.language': 'Taal',
  'common.plusMore': '+{{count}} meer',
  'common.added': 'Bygevoeg',
  'common.copyFailed': 'Kon nie kopieer nie',
  'common.unknownError': 'Onbekende fout',
  'common.name': 'Naam',
  'common.email': 'E-pos',
  'common.status': 'Status',
  'common.type': 'Tipe',
  'common.description': 'Beskrywing',
  'common.created': 'Geskep',
  'common.updated': 'Bygewerk',
  'common.actions': 'Aksies',
  'common.yes': 'Ja',
  'common.no': 'Nee',
  'common.all': 'Alle',
  'common.none': 'Geen',
  'common.search': 'Soek',
  'common.searchPlaceholder': 'Soek…',
  'common.noResults': 'Geen resultate.',
  'common.date': 'Datum',
  'common.amount': 'Bedrag',
  'common.total': 'Totaal',
  'common.details': 'Besonderhede',
  'common.overview': 'Oorsig',
  'common.optional': 'Opsioneel',
  'common.required': 'Verpligtend',
  'common.copied': 'Na knipbord gekopieer.',
  'common.enabled': 'Geaktiveer',
  'common.disabled': 'Gedeaktiveer',
  'common.never': 'Nooit',
  'common.unknown': 'Onbekend',
  'common.select': 'Kies…',
  'common.you': 'Jy',
  'common.notAvailable': 'n.v.t.',
  'action.edit': 'Wysig',
  'action.close': 'Sluit',
  'action.refresh': 'Herlaai',
  'action.retry': 'Probeer weer',
  'action.back': 'Terug',
  'action.apply': 'Pas toe',
  'action.clear': 'Maak skoon',
  'action.copy': 'Kopieer',
  'common.download': 'Laai af',
  'action.view': 'Bekyk',
  'action.confirm': 'Bevestig',
  'action.add': 'Voeg by',
  'action.remove': 'Verwyder',
  'action.submit': 'Dien in',
  'action.send': 'Stuur',
  'action.continue': 'Gaan voort',
  'action.next': 'Volgende',
  'action.previous': 'Vorige',
  'action.download': 'Laai af',
  'action.saveChanges': 'Stoor veranderinge',
  'action.discard': 'Verwerp',
  'action.select': 'Kies',
  'action.open': 'Open',
  'action.enable': 'Aktiveer',
  'action.disable': 'Deaktiveer',
  'action.activate': 'Aktiveer',
  'action.pause': 'Onderbreek',
  'action.start': 'Begin',
  'action.signIn': 'Teken in',
  'action.signUp': 'Registreer',
  'action.export': 'Voer uit',
  'action.import': 'Voer in',
  'action.revoke': 'Herroep',
  'action.regenerate': 'Herskep',
  'action.keep': 'Behou',
  'status.active': 'Aktief',
  'status.inactive': 'Onaktief',
  'status.pending': 'Hangend',
  'status.approved': 'Goedgekeur',
  'status.rejected': 'Afgekeur',
  'status.failed': 'Misluk',
  'status.sent': 'Gestuur',
  'status.delivered': 'Afgelewer',
  'status.read': 'Gelees',
  'status.draft': 'Konsep',
  'status.submitted': 'Ingedien',
  'status.paused': 'Onderbreek',
  'status.running': 'Loop tans',
  'status.completed': 'Voltooi',
  'status.cancelled': 'Gekanselleer',
  'status.queued': 'In tou',
  'status.scheduled': 'Geskeduleer',
  'status.expired': 'Verstreke',
  'status.revoked': 'Herroep',
  'status.verified': 'Geverifieer',
  'status.unverified': 'Ongeverifieer',
  'status.open': 'Oop',
  'status.closed': 'Gesluit',
  'status.resolved': 'Opgelos',
  'status.archived': 'Geargiveer',
  'status.unknown': 'Onbekend',
  'toast.saved': 'Gestoor',
  'toast.deleted': 'Geskrap',
  'toast.copied': 'Gekopieer',
  'toast.validation': 'Validasie',
  'toast.saveFailed': 'Stoor het misluk',
  'toast.deleteFailed': 'Skrap het misluk',
  'toast.loadFailed': 'Kon nie laai nie',
  'notFound.title': 'Bladsy nie gevind nie',
  'notFound.attempted': 'Jy het gevra vir',
  'notFound.didYouMean': 'Het jy bedoel',
  'notFound.description': 'Die bladsy wat jy soek, bestaan nie of het geskuif.',
  'notFound.backToDashboard': 'Terug na paneelbord',
  'country.za': 'Suid-Afrika',
  'country.ng': 'Nigerië',
  'country.ke': 'Kenia',
  'action.undo': 'Ontdoen',
  'password.rules.summary': 'Ten minste 12 karakters. Algemene uitgelekte wagwoorde word nie aanvaar nie.',
  'password.rules.length': 'Ten minste 12 karakters',
  'password.rules.upper': 'ʼn Hoofletter',
  'password.rules.lower': 'ʼn Kleinletter',
  'password.rules.digit': 'ʼn Syfer',
  'password.rules.symbol': 'ʼn Simbool',
  'password.strength.label': 'Wagwoordsterkte',
  'action.dismiss': 'Maak toe',
  'table.view.table': 'Tabel',
  'table.view.cards': 'Rooster',
  'table.view.list': 'Lys',
  'table.search': 'Soek',
  'table.perPage': 'Rye per bladsy',
  'table.perPageOption': '{{n}} / bladsy',
  'table.noRecords': 'Geen rekords',
  'table.range': '{{from}}–{{to}} van {{total}}',
  'action.loadMore': 'Laai meer',
  'phone.savedAs': 'Word gestoor as {{number}}',
  'phone.search': 'Soek volgens land of kode',
  'phone.noMatches': 'Geen land pas nie',
  'phone.country': 'Landkode',
  'phone.reason.empty': 'Voer ŉ telefoonnommer in.',
  'phone.reason.notANumber': 'ŉ Telefoonnommer bevat net syfers, en mag met + begin.',
  'phone.reason.tooShort': 'Te kort vir {{country}} (+{{dial}}): ŉ nommer daar het {{expected}} syfers ná die landkode.',
  'phone.reason.tooLong': 'Te lank vir {{country}} (+{{dial}}): ŉ nommer daar het {{expected}} syfers ná die landkode.',
  'phone.reason.unknownCountry': 'Ons ken nie daardie landkode nie. Kontroleer die syfers ná die +.',
  'phone.reason.noCountry': 'Begin met + en die landkode, byvoorbeeld +27.',
  'phone.fixNumber': 'Kontroleer die telefoonnommer voordat jy voortgaan.',
  'phone.hint': 'Tik dit soos jy dit sou skakel. Ons voeg die landkode by.',
  'common.or': 'of',
  'attributes.group.contact': 'Kontak',
  'attributes.group.fields': 'Jou velde',
  'attributes.group.flow': 'Vloeiveranderlikes',
  'attributes.contact.name': 'Naam',
  'attributes.contact.email': 'E-pos',
  'attributes.contact.msisdn': 'Telefoonnommer',
  'attributes.contact.locale': 'Taal',
  'attributes.fallbackHint': 'Voeg | en ’n terugvalwaarde ná die naam by, vir enigiemand wat niks daar het nie: contact.name|daar',
  'attributes.flowFallbackHint': 'Voeg ?? en ’n terugvalwaarde ná die naam by, vir wanneer dit leeg is: contact.name ?? daar',
  'password.errors.breached': 'Daardie wagwoord is een van die algemeenste uitgelekte wagwoorde. Kies een wat jou eie is.',
  'password.errors.tooShort': '’n Wagwoord het ten minste 12 karakters nodig.',
  'action.change': 'Verander',
  'common.words.add': 'Tik en druk Enter',
  'common.words.remove': 'Verwyder {{word}}',
  'common.words.full': 'Dis die meeste: {{max}}'
};

export default common;
